// @ts-nocheck

export enum ORDER_TYPE {
    ASC = 'asc',
    DESC = 'desc',
}

export interface IDebounceOptions {
    leading?: boolean
    maxWait?: number
    trailing?: boolean
}

export interface IDebouncedFunc<T extends (...args: any) => any> {
    (...args: Parameters<T>): ReturnType<T> | undefined

    cancel: () => void

    flush: () => ReturnType<T> | undefined

    pending: () => boolean
}

function orderBy<T>(
    arr: Array<T>,
    keys: Array<string> | string | ((x: object) => number),
    orders: Array<ORDER_TYPE> = [ORDER_TYPE.ASC],
): Array<T> {
    function sortBy(key: string, cb: () => number) {
        if (!cb) {
            cb = () => 0
        }
        return (a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : cb(a, b))
    }

    function sortByDesc(key: string, cb: () => number) {
        if (!cb) {
            cb = () => 0
        }
        return (b, a) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : cb(b, a))
    }

    let cb = (): number => 0

    if (arr === null) {
        return []
    }

    if (typeof keys === 'function') {
        return arr.reduce((acc: Array<T>, item: T) => {
            acc.splice(keys(item), 0, item)
            return acc
        }, [])
    }

    if (Object.keys(keys).length === 0) {
        return arr
    }

    !Array.isArray(keys) && (keys = keys === null ? [] : [keys])
    !Array.isArray(orders) && (orders = orders === null ? [] : [orders])

    keys.reverse()
    orders.reverse()
    for (const [i, key] of keys.entries()) {
        const order = orders[i]
        if (order === ORDER_TYPE.ASC) {
            cb = sortBy(key, cb)
        } else if (order === ORDER_TYPE.DESC) {
            cb = sortByDesc(key, cb)
        } else {
            throw new Error(`Unsupported order "${order}"`)
        }
    }
    return arr.concat().sort(cb)
}

function isEmpty(obj: any): boolean {
    return (
        typeof obj !== 'object' ||
        ([Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length)
    )
}

function uniqBy<T>(arr: Array<T>, predicate: string): Array<T> {
    const cb = (o) => o[predicate]

    return [
        ...arr
            .reduce((map, item) => {
                const key = item === null || item === undefined ? item : cb(item)

                map.has(key) || map.set(key, item)

                return map
            }, new Map())
            .values(),
    ]
}

function debounce<T, A extends (...args: any) => T>(
    func: A,
    wait = 0,
    options: IDebounceOptions = {},
): IDebouncedFunc<A> {
    let { leading = false, maxWait, trailing = true } = options

    let lastArgs: any
    let lastThis: any
    let result: T
    let timerId: ReturnType<typeof setTimeout>
    let lastCallTime: number

    let lastInvokeTime = 0
    const maxing = 'maxWait' in options
    maxWait = maxing ? Math.max(+maxWait || 0, wait) : maxWait

    if (typeof func !== 'function') {
        throw new TypeError('Expected a function')
    }

    function invokeFunc(time: number): any {
        const args = lastArgs
        const thisArg = lastThis

        lastArgs = lastThis = undefined
        lastInvokeTime = time
        result = func.apply(thisArg, args)
        return result
    }

    function startTimer(pendingFunc: any, wait: number): NodeJS.Timeout {
        return setTimeout(pendingFunc, wait)
    }

    function leadingEdge(time: number): any {
        lastInvokeTime = time
        timerId = startTimer(timerExpired, wait)
        return leading ? invokeFunc(time) : result
    }

    function remainingWait(time: number): number {
        const timeSinceLastCall = time - lastCallTime
        const timeSinceLastInvoke = time - lastInvokeTime
        const timeWaiting = wait - timeSinceLastCall

        return maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting
    }

    function shouldInvoke(time: number): boolean {
        const timeSinceLastCall = time - lastCallTime
        const timeSinceLastInvoke = time - lastInvokeTime
        return (
            lastCallTime === undefined ||
            timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 ||
            (maxing && timeSinceLastInvoke >= maxWait)
        )
    }

    function timerExpired(): any {
        const time = Date.now()
        if (shouldInvoke(time)) {
            return trailingEdge(time)
        }
        timerId = startTimer(timerExpired, remainingWait(time))
    }

    function trailingEdge(time: number): any {
        timerId = undefined
        if (trailing && lastArgs) {
            return invokeFunc(time)
        }
        lastArgs = lastThis = undefined
        return result
    }

    function cancel(): void {
        if (timerId !== undefined) {
            clearTimeout(timerId)
        }
        lastInvokeTime = 0
        lastArgs = lastCallTime = lastThis = timerId = undefined
    }

    function flush(): any {
        return timerId === undefined ? result : trailingEdge(Date.now())
    }

    function pending(): boolean {
        return timerId !== undefined
    }

    function debounced(...args: any) {
        const time = Date.now()
        const isInvoking = shouldInvoke(time)

        lastArgs = args
        lastThis = this
        lastCallTime = time

        if (isInvoking) {
            if (timerId === undefined) {
                return leadingEdge(lastCallTime)
            }
            if (maxing) {
                timerId = startTimer(timerExpired, wait)
                return invokeFunc(lastCallTime)
            }
        }
        if (timerId === undefined) {
            timerId = startTimer(timerExpired, wait)
        }
        return result
    }

    debounced.cancel = cancel
    debounced.flush = flush
    debounced.pending = pending
    return debounced
}

function throttle(func: () => any, wait: number): ReturnType<typeof debounce> {
    const leading = true
    const trailing = true

    if (typeof func !== 'function') {
        throw new TypeError('Expected a function')
    }
    return debounce(func, wait, {
        leading,
        trailing,
        maxWait: wait,
    })
}

function typeCheck(value): string {
    const return_value = Object.prototype.toString.call(value)

    const type = return_value.substring(return_value.indexOf(' ') + 1, return_value.indexOf(']'))

    return type.toLowerCase()
}

export interface GetSafeArgs<Obj, Fallback, ReturnValueType> {
    object: Obj
    path: string
    fallback: Fallback
    returnValueType?: ReturnValueType
}

/**
 * Returns required property from the object
 * ( if the property does not exist or has undesirable type, the function returns the default value )
 *@param object - the object to be searched:
 *@param path - the path to find the property you want
 *@param fallback - default value (this value will be returned if object does not have property you want)
 *@param returnValueType - this param indicates the type of return value ( if value type is incompatible with this parameter, the function returns default value (fallback) )
 */
function getSafe<Args extends GetSafeArgs>(args: Args): Args[ReturnValueType] {
    const { object, path, fallback, returnValueType } = args

    if (!object) {
        return fallback
    }

    const fragments = path.split('.')
    let value = args.object

    for (let i = 0; i < fragments.length; i++) {
        const searchInTheArray = fragments[i].includes('[') && fragments[i].includes(']')

        if (searchInTheArray) {
            const findEverythingBeforeSquareBracket = /(.*)\[[^\][]*\](.*)/
            const findValueFromSquareBrackets = /(\w+)\s?(?:\[([^\]]+)\])?/

            const index = fragments[i].match(findValueFromSquareBrackets)[2]
            const arrayName = fragments[i].match(findEverythingBeforeSquareBracket)[1]

            if (Number.isNaN(index)) {
                return fallback
            }
            if (!arrayName) {
                return fallback
            }
            if (!object[arrayName]) {
                return fallback
            }
            if (object[arrayName].length === 0) {
                return fallback
            }
            if (index > object[arrayName].length) {
                return fallback
            }

            value = value[arrayName][index]
        } else if (Object.prototype.hasOwnProperty.call(value, fragments[i])) {
            value = value[fragments[i]]
        } else {
            return fallback
        }
    }

    // @TODO check array types
    if (returnValueType && typeCheck(value) !== returnValueType) {
        return fallback
    }

    return value
}

function fillArrNum(start: number, end: number): Array<number> {
    const a = []
    for (let i = start; i < end; i++) {
        a.push(i)
    }
    return a
}

// TODO: FIX UUID
function uuidv4(): string {
    let d = new Date().getTime() // Timestamp
    let d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0 // Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        let r = Math.random() * 16 // random number between 0 and 16
        if (d > 0) {
            // Use timestamp until depleted
            r = (d + r) % 16 | 0
            d = Math.floor(d / 16)
        } else {
            // Use microseconds since page-load if supported
            r = (d2 + r) % 16 | 0
            d2 = Math.floor(d2 / 16)
        }
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
}

function isUuidV4(stringToTest: string): boolean {
    const regEx = /[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89aAbB][a-f0-9]{3}-[a-f0-9]{12}/
    return regEx.test(stringToTest)
}

function capitalizeFirstLetter(value: string): string {
    if (!value) {
        return value
    }
    return value[0].toUpperCase() + value.slice(1)
}

// const decodeBase64 = (str: string): string => decodeURIComponent(escape(window.atob(str)));

// const encodeToBase64 = (str: string): string => window.btoa(unescape(encodeURIComponent(str)));

const isDate = (date: Date): boolean => date instanceof Date

/**
 * Converts the given enum to a map of the keys to the values.
 * @param enumeration The enum to convert to a map.
 */
function enumToMap(enumeration: any): Map<string, string | number> {
    const map = new Map<string, string | number>()
    for (const key in enumeration) {
        if (!isNaN(Number(key))) {
            continue
        }

        const val = enumeration[key] as string | number

        if (val !== undefined && val !== null) {
            map.set(key, val)
        }
    }

    return map
}

/**
 * Create timeout to be used in Promise.race conditions
 *
 * @param {number} seconds
 * @return {*}  {Promise<{ timeout: true }>}
 */
function sleep(seconds: number): Promise<{ timeout: true }> {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            resolve({ timeout: true })
        }, seconds * 1000)
    })
}

function objectToString(obj) {
    // Jeśli obj nie jest obiektem, zwróć go jako string
    if (typeof obj !== 'object' || obj === null) {
        return String(obj)
    }

    // Jeśli obj jest tablicą
    if (Array.isArray(obj)) {
        const results = obj.map((item) => objectToString(item))
        return `[${results.join(', ')}]`
    }

    // Jeśli obj jest obiektem
    const results = []
    for (const key in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(key)) {
            results.push(`${key}: ${objectToString(obj[key])}`)
        }
    }
    return `{${results.join(', ')}}`
}

enum EDataType {
    Array = 'array',
    Object = 'object',
    String = 'string',
    Number = 'number',
    Boolean = 'boolean',
    Function = 'function',
    Null = 'null',
    Undefined = 'undefined',
    Unknown = 'unknown',
}

function determineType(data: any): EDataType {
    if (Array.isArray(data)) {
        return EDataType.Array
    }
    if (data === null) {
        return EDataType.Null
    }

    const typeMapping: Record<string, EDataType> = {
        object: EDataType.Object,
        string: EDataType.String,
        number: EDataType.Number,
        boolean: EDataType.Boolean,
        function: EDataType.Function,
        undefined: EDataType.Undefined,
    }

    return typeMapping[typeof data] || EDataType.Unknown
}

export {
    orderBy,
    orderBy as sortBy,
    isEmpty,
    uniqBy,
    throttle,
    debounce,
    getSafe,
    fillArrNum,
    uuidv4,
    isUuidV4,
    capitalizeFirstLetter,
    sleep,
    isDate,
    enumToMap,
    objectToString,
    determineType,
    EDataType,
}
